import { isProtectedMemoryCategory } from '../../../memory/taxonomy';
import { selectWithinByteBudget } from './budget';
import type { RankedMemory } from './types';

/**
 * Relative-chronological memory-block rendering — a faithful port of the
 * sister platform's `relative_chronological.j2` + `MemoryFormatter`:
 * one timestamped line per memory, retaining protected-first admission
 * order and recency within each protection tier. Non-protected inferences
 * are held separately as hypotheses.
 *
 * Parameterized on the header/empty-state strings and the byte budget so it
 * has no import-time dependency on the provider types module; the provider
 * wires the shared constants in.
 */

export interface RenderOptions {
  /** Hard byte ceiling for the rendered entries (ADR-075 Rule 5: ≤ 20 KB). */
  maxBytes: number;
  /** First line of a non-empty block (kept in shape-parity with the empty state). */
  header: string;
  /** Canonical empty state — returned verbatim for zero retrievable memories. */
  emptyBlock: string;
  /** Anchor for time-ago rendering (defaults to wall clock). */
  now?: () => Date;
}

const HYPOTHESES_HEADER = "Open questions you're holding about them";
const HYPOTHESES_GUIDANCE =
  '(Working hypotheses from earlier sessions — not facts. Let them guide your curiosity; confirm, refine, or drop them. Never state them as truths.)';
const HYPOTHESES_PREFIX = `${HYPOTHESES_HEADER}\n${HYPOTHESES_GUIDANCE}\n`;
const HYPOTHESIS_QUALIFIER = 'Working hypothesis (not a fact; confirm, refine, or drop): ';

function isWorkingHypothesis(memory: RankedMemory): boolean {
  return memory.confidenceTier === 'inferred' && !isProtectedMemoryCategory(memory.category);
}

/** Recency anchor: last corroboration, else last seen, else creation. */
export function recencyTimestamp(memory: RankedMemory): number {
  return (memory.lastCorroboratedAt ?? memory.lastSeenAt ?? memory.createdAt).getTime();
}

/** Ported human time-ago buckets (elapsed-day granularity, no local clock claim). */
export function formatTimeAgo(days: number): string {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'This week';
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
}

function titleCase(category: string): string {
  return category
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function renderLine(memory: RankedMemory, now: Date): string {
  const at = new Date(recencyTimestamp(memory));
  const days = Math.floor((now.getTime() - at.getTime()) / 86_400_000);
  const qualifiers: string[] = [titleCase(memory.category)];
  if (memory.confidenceTier && memory.confidenceTier !== 'explicit') {
    qualifiers.push(memory.confidenceTier);
  }
  if (memory.verification === 'disputed') qualifiers.push('disputed');
  // Newline-normalized: memory content is LLM-extracted from member speech
  // and lands verbatim in future system prompts — quote-grounding proves the
  // member SAID it, not that it's benign. Collapsing line breaks means a
  // stored multi-line payload can never forge additional block entries or
  // freestanding directives; each memory is structurally one `- […]` line.
  const singleLine = memory.content.replace(/\s*[\r\n\u2028\u2029]+\s*/g, ' ').trim();
  // Keep the uncertainty on each entry as well as the section: consumers
  // that flatten the block must not silently promote a hypothesis to fact.
  const hypothesisLabel = isWorkingHypothesis(memory) ? HYPOTHESIS_QUALIFIER : '';
  return `- [${formatTimeAgo(days)}] ${hypothesisLabel}${singleLine} (${qualifiers.join(', ')})`;
}

/**
 * Compose the final block: budget-select (protected-first best-fit, whole
 * memories only), then retain that order within each section. Zero
 * retrievable/kept memories render the canonical empty state verbatim so the
 * prompt keeps a stable shape.
 *
 * Lines are pre-rendered so the walker's cost is exact, including separators
 * and a section's heading/guidance only when its first entry is admitted.
 * An unselected hypothesis must never reserve space ahead of a protected
 * fact. Exact UTF-8 costs make the ceiling hold by construction.
 * `utf8RenderCost` remains the estimator for callers without a render.
 */
export function composeMemoryBlock(memories: RankedMemory[], options: RenderOptions): string {
  if (memories.length === 0) return options.emptyBlock;

  const now = (options.now ?? (() => new Date()))();
  const renderedLines = new Map<string, string>(memories.map((m) => [m.id, renderLine(m, now)]));

  const headerCost = Buffer.byteLength(`${options.header}\n\n`, 'utf-8');
  const hypothesesPrefixCost = Buffer.byteLength(HYPOTHESES_PREFIX, 'utf-8');
  const admittedSections = new Set<boolean>();
  const selection = selectWithinByteBudget(memories, options.maxBytes - headerCost, {
    costFn: (m) => {
      const hypothesis = isWorkingHypothesis(m);
      const sameSection = admittedSections.has(hypothesis);
      const separatorCost = sameSection ? 1 : admittedSections.size > 0 ? 2 : 0;
      const sectionCost = hypothesis && !sameSection ? hypothesesPrefixCost : 0;
      return (
        Buffer.byteLength(renderedLines.get(m.id) ?? '', 'utf-8') + separatorCost + sectionCost
      );
    },
    onKeepFn: (m) => admittedSections.add(isWorkingHypothesis(m)),
    isProtectedFn: (m) => isProtectedMemoryCategory(m.category),
    recencyFn: recencyTimestamp,
  });
  if (selection.kept.length === 0) return options.emptyBlock;

  const facts = selection.kept
    .filter((m) => !isWorkingHypothesis(m))
    .map((m) => renderedLines.get(m.id) ?? '');
  const hypotheses = selection.kept
    .filter(isWorkingHypothesis)
    .map((m) => renderedLines.get(m.id) ?? '');
  const sections: string[] = [];
  if (facts.length > 0) sections.push(facts.join('\n'));
  if (hypotheses.length > 0) sections.push(`${HYPOTHESES_PREFIX}${hypotheses.join('\n')}`);

  return `${options.header}\n\n${sections.join('\n\n')}`;
}
